import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { RENEWAL_MATCH_SUGGEST_THRESHOLD } from "@shared/utils/planNameMatch";
import { SystemPlanCombobox } from "./SystemPlanCombobox";

/**
 * Renew one of a client's plans for its next plan year, in four steps:
 *
 * 1. Plan — which plan the new year is on. A maintained (Administr library)
 *    plan renews onto next year's version of it, suggested by name; a custom
 *    plan renews onto a copy of itself that can be renamed. The current plan
 *    year is never changed and stays in force until the new one starts.
 * 2. Rates — the renewal date and (medical) the rating area the new year is
 *    priced on.
 * 3. Contributions — carry the client's contributions over, or set them up
 *    afterwards.
 * 4. Enrollments — pre-enroll everyone currently covered into the new plan
 *    year, so open enrollment starts from what they have today.
 */
export interface RenewPlanDialogPlan {
  /** Library plan id. */
  id: number;
  /** The plan year (client_plans link) being renewed. */
  clientPlanId: number;
  name: string;
  type?: string | null;
  carrierId?: number | null;
}

interface RenewPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: RenewPlanDialogPlan | null;
  clientId: string;
  onRenewed?: (result: any) => void;
}

type PlanChoice = "copy" | "same" | "other" | `plan:${number}`;

const STEPS = ["Plan", "Rates", "Contributions", "Enrollments"] as const;

/** "Dental PPO 2025" → "Dental PPO 2026" when the name carries the old plan year. */
function nameForNewYear(name: string, newStartIso: string | undefined): string {
  const newYear = Number((newStartIso || "").slice(0, 4));
  if (!Number.isFinite(newYear) || newYear < 2000) return name;
  return name.replace(new RegExp(`\\b${newYear - 1}\\b`, "g"), String(newYear));
}

async function getJson(url: string) {
  const res = await fetch(url, { credentials: "include" });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || "Request failed");
  return body;
}

export function RenewPlanDialog({ open, onOpenChange, plan, clientId, onRenewed }: RenewPlanDialogProps) {
  const { toast } = useToast();
  const linkId = plan?.clientPlanId;
  const isHealth = ["health", "medical"].includes(String(plan?.type || "").toLowerCase());

  const [step, setStep] = useState(0);
  const [choice, setChoice] = useState<PlanChoice | null>(null);
  const [otherPlanId, setOtherPlanId] = useState<number | null>(null);
  // Null until the broker types: the default follows the plan and renewal date.
  const [copyNameEdit, setCopyNameEdit] = useState<string | null>(null);
  const [newStart, setNewStart] = useState("");
  const [areaId, setAreaId] = useState<string>("client");
  const [carryContributions, setCarryContributions] = useState(true);
  const [carryEnrollments, setCarryEnrollments] = useState(true);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setChoice(null);
    setOtherPlanId(null);
    setCopyNameEdit(null);
    setNewStart("");
    setAreaId("client");
    setCarryContributions(true);
    setCarryEnrollments(true);
  }, [open, linkId]);

  // What renewing in place would look like — gives the default renewal date,
  // whether this is a custom plan, and whether the same row can take another year.
  const { data: base } = useQuery<any>({
    queryKey: ["/api/client-plans", linkId, "renewal-preview", "base"],
    queryFn: () => getJson(`/api/client-plans/${linkId}/renewal-preview`),
    enabled: open && !!linkId,
  });
  const effectiveStart = newStart || base?.newEffectiveStart || "";

  const { data: candidateData, isLoading: candidatesLoading } = useQuery<any>({
    queryKey: ["/api/client-plans", linkId, "renewal-candidates", effectiveStart],
    queryFn: () =>
      getJson(`/api/client-plans/${linkId}/renewal-candidates?newEffectiveStart=${effectiveStart}`),
    enabled: open && !!linkId && !!effectiveStart,
  });
  const candidates: any[] = (candidateData?.candidates || []).slice(0, 5);
  const suggested =
    candidates[0] && candidates[0].score >= RENEWAL_MATCH_SUGGEST_THRESHOLD ? candidates[0] : null;
  const canKeepSamePlan = base != null && base.planYearProblem == null;

  // Default choice, once: the suggested match when there is one; otherwise a
  // custom plan renews onto a copy of itself.
  useEffect(() => {
    if (!open || choice != null || !base || candidatesLoading) return;
    if (suggested) setChoice(`plan:${suggested.id}`);
    else if (base.isCustomPlan) setChoice("copy");
  }, [open, choice, base, candidatesLoading, suggested]);
  const copyName = copyNameEdit ?? nameForNewYear(plan?.name ?? "", effectiveStart);

  const targetPlanId =
    choice === "other" ? otherPlanId : choice?.startsWith("plan:") ? Number(choice.slice(5)) : null;
  const planChosen = choice === "copy" || choice === "same" || targetPlanId != null;

  const { data: clientInfo } = useQuery<any>({
    queryKey: ["/api/clients", clientId, "renew"],
    queryFn: () => getJson(`/api/clients/${clientId}`),
    enabled: open && isHealth,
  });
  const areaState = String(clientInfo?.state || "").trim().toUpperCase();
  const { data: stateAreas = [] } = useQuery<any[]>({
    queryKey: ["/api/rating-areas", areaState || null],
    queryFn: () => getJson(`/api/rating-areas?state=${areaState}`),
    enabled: open && isHealth && /^[A-Z]{2}$/.test(areaState),
  });
  const uniqueAreas = useMemo(() => {
    const byId = new Map<string, any>();
    for (const a of stateAreas) if (!byId.has(String(a.id))) byId.set(String(a.id), a);
    return Array.from(byId.values());
  }, [stateAreas]);

  const choiceParams = useMemo(() => {
    const body: Record<string, any> = {};
    if (effectiveStart) body.newEffectiveStart = effectiveStart;
    if (choice === "copy") body.copyPlan = true;
    else if (targetPlanId != null) body.targetPlanId = targetPlanId;
    if (isHealth && areaId !== "client" && areaState) {
      body.ratingAreaOverride = areaId;
      body.ratingAreaState = areaState;
    }
    return body;
  }, [effectiveStart, choice, targetPlanId, isHealth, areaId, areaState]);

  const { data: preview, isFetching: previewLoading } = useQuery<any>({
    queryKey: ["/api/client-plans", linkId, "renewal-preview", choiceParams],
    queryFn: () =>
      getJson(
        `/api/client-plans/${linkId}/renewal-preview?${new URLSearchParams(
          Object.entries(choiceParams).map(([k, v]) => [k, String(v)]),
        ).toString()}`,
      ),
    enabled: open && !!linkId && planChosen,
  });

  const sameRow = preview?.mode === "same";
  const contributions = preview?.contributions;
  const canCarryContributions = !sameRow && !!contributions?.canCarryOver;
  const enrolledCount: number = preview?.enrolledCount ?? 0;
  const isFsa = isFsaPlanType(plan?.type);
  const canCarryEnrollments = !sameRow && !isFsa && enrolledCount > 0;

  const renewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/client-plans/${linkId}/renew`, {
        ...choiceParams,
        planName: choice === "copy" ? copyName.trim() || undefined : undefined,
        carryOverContributions: canCarryContributions && carryContributions,
        carryOverEnrollments: canCarryEnrollments && carryEnrollments,
      });
      return res.json();
    },
    onSuccess: (result: any) => {
      const p = result?.preview;
      const parts: string[] = [
        result?.alreadyRenewed
          ? `This plan was already renewed for the plan year starting ${p?.newEffectiveStart}.`
          : `Added the plan year starting ${p?.newEffectiveStart}. The current plan year stays in force until then.`,
      ];
      if (result?.contributionsCarried) parts.push("Contributions were carried over.");
      else if (!result?.alreadyRenewed && !sameRow && !contributions?.targetConfigured) {
        parts.push("Contributions still need to be set up on the new plan year.");
      }
      const carried = result?.enrollments;
      if (carried) {
        parts.push(
          carried.notApplicable ??
            `${carried.carried} employee${carried.carried === 1 ? "" : "s"} pre-enrolled for the new plan year.`,
        );
        if (carried.skipped?.length) {
          parts.push(`Not carried over: ${carried.skipped.map((s: any) => `${s.name} (${s.reason})`).join(", ")}.`);
        }
      }
      if (choice === "copy") parts.push("Review the copied rates on the new plan.");
      if (p?.reaffirmationRequired?.length) {
        parts.push(`${p.reaffirmationRequired.length} member(s) need tobacco/RAS re-affirmation.`);
      }
      toast({ title: "Plan year renewed", description: parts.join(" "), variant: "success", duration: 12000 });
      onRenewed?.(result);
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to renew plan",
        description: String(error?.message || "Please try again."),
        variant: "destructive",
      });
    },
  });

  // What stops each step from moving on.
  const stepBlocker: string | null = (() => {
    if (step === 0) {
      if (!planChosen) return "Pick the plan for the new plan year.";
      if (choice === "copy" && !copyName.trim()) return "Give the new plan year's plan a name.";
      return null;
    }
    if (!preview || previewLoading) return "Checking…";
    if (step === 1) {
      if (preview.planYearProblem) return preview.planYearProblem;
      if (preview.planRetired) return `"${preview.targetPlanName}" is retired. Pick a replacement plan.`;
    }
    return null;
  })();

  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Renew {plan?.name}</DialogTitle>
          <DialogDescription>
            Sets this plan up for the next plan year. The current plan year is not changed and stays
            in force until the new one starts.
          </DialogDescription>
        </DialogHeader>

        <ol className="flex items-center gap-2 text-xs">
          {STEPS.map((label, i) => (
            <li
              key={label}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${
                i === step
                  ? "border-primary bg-primary/10 font-medium text-foreground"
                  : i < step
                    ? "text-foreground"
                    : "text-muted-foreground"
              }`}
            >
              {i < step ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <span>{i + 1}.</span>}
              {label}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {candidates.length > 0
                ? "Pick the plan for the new plan year — next year's version from the library, or a copy of this plan."
                : base?.isCustomPlan
                  ? "This is a custom plan, so the new plan year gets its own copy — rename it or keep the name."
                  : "Pick next year's version of this plan from the library."}
            </p>
            <RadioGroup
              value={choice ?? ""}
              onValueChange={(v) => setChoice(v as PlanChoice)}
              className="space-y-2"
            >
              {candidates.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                  <RadioGroupItem value={`plan:${c.id}`} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {c.name}
                      {suggested?.id === c.id && (
                        <Badge variant="outline" className="border-green-200 bg-green-50 text-[10px] text-green-700">
                          Suggested match
                        </Badge>
                      )}
                      {c.isCustomPlan && (
                        <Badge variant="outline" className="text-[10px]">
                          Custom
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {c.planCode && <span className="font-mono">{c.planCode}</span>}
                      {c.planCode && c.planYearStart && " · "}
                      {c.planYearStart && `Plan year ${c.planYearStart} to ${c.planYearEnd ?? "open"}`}
                    </p>
                    {!c.hasRates && (
                      <p className="text-[11px] text-amber-600">No rates on file for this client yet</p>
                    )}
                  </div>
                </label>
              ))}

              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="other" className="mt-0.5" />
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-sm font-medium">Pick another plan</p>
                  {choice === "other" && plan && (
                    <SystemPlanCombobox
                      value={otherPlanId}
                      onSelect={(p) => setOtherPlanId(p?.id ?? null)}
                      clientId={Number(clientId)}
                      typeFilter={
                        isHealth ? ["health", "medical"] : plan.type ? [String(plan.type)] : undefined
                      }
                      carrierIdFilter={plan.carrierId ?? null}
                      effectiveDateContains={effectiveStart || null}
                      placeholder="Search the library…"
                    />
                  )}
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="copy" className="mt-0.5" />
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-sm font-medium">Copy this plan for the new plan year</p>
                  <p className="text-xs text-muted-foreground">
                    Benefits and today's rates are copied as the starting point; update the rates
                    afterwards if they changed.
                  </p>
                  {choice === "copy" && (
                    <div>
                      <Label className="text-xs">Plan name for the new plan year</Label>
                      <Input
                        className="mt-1 h-9"
                        value={copyName}
                        onChange={(e) => setCopyNameEdit(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </label>

              {canKeepSamePlan && (
                <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                  <RadioGroupItem value="same" className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Keep the same plan</p>
                    <p className="text-xs text-muted-foreground">
                      Adds another plan year of this same plan. Its details and contributions are
                      shared between both years.
                    </p>
                  </div>
                </label>
              )}
            </RadioGroup>
            {!candidatesLoading && candidates.length === 0 && !base?.isCustomPlan && (
              <p className="text-xs text-muted-foreground">
                No plan from this carrier in the library covers the new plan year yet.
              </p>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-sm">Renewal date</Label>
                <Input
                  type="date"
                  className="mt-1 h-10"
                  value={effectiveStart}
                  onChange={(e) => setNewStart(e.target.value)}
                />
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  First day of the new plan year. The current one ends the day before.
                </p>
              </div>
              {isHealth && (
                <div>
                  <Label className="text-sm">Rating area</Label>
                  <Select value={areaId} onValueChange={setAreaId}>
                    <SelectTrigger className="mt-1 h-10 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client's primary location</SelectItem>
                      {uniqueAreas.map((a: any) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {a.label || `Area ${a.areaNumber ?? a.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {preview?.toRatingAreaId != null
                      ? `The new plan year rates in area ${preview.toRatingAreaId}${
                          preview.ratingAreaWillChange
                            ? ` (currently ${preview.fromRatingAreaId ?? "not set"}).`
                            : ", same as now."
                        }`
                      : "No rating area resolves from this location."}
                  </p>
                </div>
              )}
            </div>

            {preview?.planYearProblem ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {preview.planYearProblem} Go back and pick the plan for that plan year, or change
                  the date.
                </AlertDescription>
              </Alert>
            ) : preview?.mode === "copy" ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {preview.ratesAvailable
                    ? "Today's rates are copied onto the new plan year. If the carrier changed them, update them on the new plan's Rates tab — it is flagged for rate review until you do."
                    : "This plan has no rates to copy. Enter the new plan year's rates on its Rates tab after renewing."}
                </AlertDescription>
              </Alert>
            ) : preview && !preview.ratesAvailable ? (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  "{preview.targetPlanName}" has no rates on file for this client yet. You can still
                  renew, but premiums won't calculate until rates are added.
                </AlertDescription>
              </Alert>
            ) : preview ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Rates come from "{preview.targetPlanName}"
                  {preview.toVersion?.effectiveStart
                    ? `, effective ${preview.toVersion.effectiveStart}.`
                    : "."}
                  {preview.existingTargetLinkId != null &&
                    " This plan is already on the client for that plan year and will be used as is."}
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-3">
            {sameRow ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  The same plan keeps the same contributions — they apply to both plan years.
                </AlertDescription>
              </Alert>
            ) : contributions?.targetConfigured ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  "{preview.targetPlanName}" already has contributions set up for this client. They
                  are kept as they are.
                </AlertDescription>
              </Alert>
            ) : !contributions?.configured ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  No contributions are set up on the current plan, so there is nothing to carry over.
                  The new plan year will show "Setup Required" until contributions are added.
                </AlertDescription>
              </Alert>
            ) : canCarryContributions ? (
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                <Checkbox
                  className="mt-0.5"
                  checked={carryContributions}
                  onCheckedChange={(v) => setCarryContributions(Boolean(v))}
                />
                <div>
                  <p className="text-sm font-medium">Carry over contributions</p>
                  <p className="text-xs text-muted-foreground">
                    Copies this client's contribution setup to the new plan year. Percentages apply
                    to the new rates.
                    {contributions.basePlans?.length > 0 &&
                      ` Base plan: ${contributions.basePlans.map((b: any) => b.name).join(", ")}, already renewed.`}
                    {" "}Leave unchecked to set contributions up later.
                  </p>
                </div>
              </label>
            ) : (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  {contributions.reason} Continuing skips contributions for now; the new plan year
                  will show "Setup Required".
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === 3 && preview && (
          <div className="space-y-3">
            {sameRow ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Current enrollments stay on this plan and continue into the new plan year.
                </AlertDescription>
              </Alert>
            ) : isFsa ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  FSA elections are made fresh each plan year, so they are not carried over.
                  Employees elect their new amount during open enrollment.
                </AlertDescription>
              </Alert>
            ) : enrolledCount === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>Nobody is enrolled in this plan, so there is nothing to carry over.</AlertDescription>
              </Alert>
            ) : (
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                <Checkbox
                  className="mt-0.5"
                  checked={carryEnrollments}
                  onCheckedChange={(v) => setCarryEnrollments(Boolean(v))}
                />
                <div>
                  <p className="text-sm font-medium">
                    Carry over {enrolledCount} enrollment{enrolledCount === 1 ? "" : "s"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Pre-enrolls everyone currently on this plan (with their dependents) into the new
                    plan year. During open enrollment they see it pre-selected and can still change
                    it; anyone who doesn't make an election stays on it. Coverage on the current
                    plan ends the day before the new plan year.
                  </p>
                </div>
              </label>
            )}
            {preview.reaffirmationRequired?.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {preview.reaffirmationRequired.length} enrolled member(s) need their tobacco / cessation
                status re-affirmed for the new plan year.
              </p>
            )}
          </div>
        )}

        {stepBlocker && stepBlocker !== "Checking…" && step !== 1 && (
          <p className="text-sm text-muted-foreground">{stepBlocker}</p>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))}>
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {isLast ? (
            <Button disabled={!!stepBlocker || renewMutation.isPending} onClick={() => renewMutation.mutate()}>
              {renewMutation.isPending ? "Renewing…" : "Renew plan"}
            </Button>
          ) : (
            <Button disabled={!!stepBlocker} onClick={() => setStep(step + 1)}>
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
